import { useMemo } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import ScreenLayout from '../screen/ScreenLayout';
import Vote from '../Vote';
import { ActiveParticipant } from '../Participant';
import {
  DevMaxiNoSessionProvider,
  LayoutPreviewProvider,
  LAYOUT_PREVIEW_SESSION_ID,
} from '../../lib/layoutPreviewContext';
import {
  buildLayoutPreviewPreset,
  PARTICIPANT_LAYOUT_PREVIEW_ME_ID,
} from '../../lib/layoutPreviewPresets';

const screenLinks: { preset: string; label: string; sub?: string }[] = [
  { preset: 'slots-writing-2', label: 'Maxi · O2 · SCRITTURA PROMPT' },
  { preset: 'slots-writing-4', label: 'Maxi · O4 · SCRITTURA PROMPT' },
  { preset: 'slots-generating-2', label: 'Maxi · O2 · GENERAZIONE IMMAGINE' },
  { preset: 'slots-generating-4', label: 'Maxi · O4 · GENERAZIONE IMMAGINE' },
  { preset: 'slots-generated-2', label: 'Maxi · O2 · post-gen (countdown verso voto)' },
  { preset: 'slots-generated-4', label: 'Maxi · O4 · post-gen (countdown verso voto)' },
  { preset: 'slots-voting-2', label: 'Maxi · O2 · VOTO IN CORSO' },
  { preset: 'slots-voting-4', label: 'Maxi · O4 · VOTO IN CORSO' },
  { preset: 'slots-winner-2', label: 'Maxi · O2 · VINCITORE' },
  { preset: 'slots-winner-4', label: 'Maxi · O4 · VINCITORE' },
  { preset: 'slots-nowinner-2', label: 'Maxi · O2 · NESSUN VINCITORE' },
  { preset: 'slots-nowinner-4', label: 'Maxi · O4 · NESSUN VINCITORE' },
  { preset: 'slots-nowinner-parity-2', label: 'Maxi · O2 · NESSUN VINCITORE (parità)' },
  { preset: 'slots-nowinner-parity-4', label: 'Maxi · O4 · NESSUN VINCITORE (parità 4)' },
  { preset: 'timer-countdown-on', label: 'Maxi · COUNTDOWN attivo', sub: '/timer' },
  { preset: 'timer-countdown-off', label: 'Maxi · COUNTDOWN non attivo', sub: '/timer' },
  { preset: 'theme-countdown', label: 'Maxi · TEMA + COUNTDOWN', sub: '/theme' },
];

const participantLinks: { preset: string; label: string }[] = [
  { preset: 'participant-waiting', label: 'Partecipante · round non attivo (IDLE)' },
  { preset: 'participant-writing', label: 'Partecipante · SCRITTURA PROMPT' },
  { preset: 'participant-generating', label: 'Partecipante · GENERAZIONE IMMAGINE' },
  {
    preset: 'participant-generated-countdown',
    label: 'Partecipante · post-gen (countdown verso voto)',
  },
  { preset: 'participant-voting', label: 'Partecipante · VOTO' },
  { preset: 'participant-winner', label: 'Partecipante · VINCITORE' },
  { preset: 'participant-nowinner', label: 'Partecipante · NESSUN VINCITORE (parità)' },
];

const voteLinks: { id: string; label: string }[] = [
  { id: 'vote-not-open', label: 'Voto · non ancora aperto' },
  { id: 'vote-active', label: 'Voto · votazione attiva' },
  { id: 'vote-voted', label: 'Voto · hai già votato' },
  { id: 'vote-closed', label: 'Voto · chiusa' },
];

export function LayoutPreviewHome() {
  return (
    <div style={{ padding: '1.5rem', maxWidth: 720, margin: '0 auto', color: 'var(--ink)' }}>
      <h1 style={{ fontSize: '1.25rem', marginBottom: '0.5rem' }}>Layout preview (dev)</h1>
      <p style={{ color: 'var(--ink-muted)', marginBottom: '1.5rem', lineHeight: 1.5 }}>
        Stessi componenti delle route reali (<code>/screen</code>, <code>/participant</code>,{' '}
        <code>/vote</code>) con stato mock. Modifiche a CSS/componenti si riflettono qui e in produzione.
      </p>

      <section style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>Maxischermo</h2>
        <ul style={{ listStyle: 'disc', paddingLeft: '1.25rem', lineHeight: 1.8 }}>
          <li>
            <Link to="/dev/layout/screen/no-session">Gara non attiva (nessuna sessione)</Link>
          </li>
          {screenLinks.map(({ preset, label, sub }) => (
            <li key={preset}>
              <Link to={`/dev/layout/screen/${preset}${sub ?? ''}`}>{label}</Link>
            </li>
          ))}
        </ul>
      </section>

      <section style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>Partecipante</h2>
        <ul style={{ listStyle: 'disc', paddingLeft: '1.25rem', lineHeight: 1.8 }}>
          {participantLinks.map(({ preset, label }) => (
            <li key={preset}>
              <Link to={`/dev/layout/participant/${preset}`}>{label}</Link>
            </li>
          ))}
        </ul>
      </section>

      <section>
        <h2 style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>Voto pubblico</h2>
        <ul style={{ listStyle: 'disc', paddingLeft: '1.25rem', lineHeight: 1.8 }}>
          {voteLinks.map(({ id, label }) => (
            <li key={id}>
              <Link to={`/dev/layout/vote/${id}`}>{label}</Link>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

export function LayoutPreviewScreenShell() {
  const { presetId } = useParams();
  const value = useMemo(() => buildLayoutPreviewPreset(presetId ?? ''), [presetId]);
  if (!value) return <Navigate to="/dev/layout" replace />;
  return (
    <LayoutPreviewProvider value={value}>
      <ScreenLayout />
    </LayoutPreviewProvider>
  );
}

export function LayoutPreviewNoSessionScreen() {
  return (
    <DevMaxiNoSessionProvider>
      <ScreenLayout />
    </DevMaxiNoSessionProvider>
  );
}

export function LayoutPreviewParticipantShell() {
  const { presetId } = useParams();
  const value = useMemo(() => buildLayoutPreviewPreset(presetId ?? ''), [presetId]);
  if (!value) return <Navigate to="/dev/layout" replace />;
  return (
    <LayoutPreviewProvider value={value}>
      <ActiveParticipant
        session={{
          sessionId: LAYOUT_PREVIEW_SESSION_ID,
          participantId: PARTICIPANT_LAYOUT_PREVIEW_ME_ID,
          slot: 1,
        }}
        onLeave={() => {}}
      />
    </LayoutPreviewProvider>
  );
}

export function LayoutPreviewVoteShell() {
  const { presetId } = useParams();
  const value = useMemo(() => buildLayoutPreviewPreset(presetId ?? ''), [presetId]);
  if (!value) return <Navigate to="/dev/layout" replace />;
  return (
    <LayoutPreviewProvider value={value}>
      <Vote key={presetId} />
    </LayoutPreviewProvider>
  );
}
